import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface User {
  id: string;
  name: string;
  mobile: string;
  email?: string;
  emailVerified?: boolean;
  role?: 'user' | 'author' | 'admin' | 'sales' | 'superadmin';
  firstName?: string;
  lastName?: string;
  profileImage?: string;
  profileComplete?: boolean;
  phoneVerified?: boolean;
}

export interface GooglePrefillData {
  email: string;
  firstName: string;
  lastName: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  otpSent: boolean;
  otpVerified: boolean;
  mobileNumber: string;
  googlePrefillData: GooglePrefillData | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: false,
  error: null,
  otpSent: false,
  otpVerified: false,
  mobileNumber: '',
  googlePrefillData: null
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setMobileNumber: (state, action: PayloadAction<string>) => {
      state.mobileNumber = action.payload;
    },
    setOtpSent: (state, action: PayloadAction<boolean>) => {
      state.otpSent = action.payload;
    },
    setOtpVerified: (state, action: PayloadAction<boolean>) => {
      state.otpVerified = action.payload;
    },
    loginSuccess: (state, action: PayloadAction<User>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.isLoading = false;
      state.error = null;
      state.otpSent = false;
      state.otpVerified = false;
      state.mobileNumber = '';
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.isLoading = false;
      state.error = null;
      state.otpSent = false;
      state.otpVerified = false;
      state.mobileNumber = '';
    },
    clearError: (state) => {
      state.error = null;
    },
    setGooglePrefillData: (state, action: PayloadAction<GooglePrefillData>) => {
      state.googlePrefillData = action.payload;
    },
    clearGooglePrefillData: (state) => {
      state.googlePrefillData = null;
    },
    setEmailVerified: (state, action: PayloadAction<boolean>) => {
      if (state.user) {
        state.user.emailVerified = action.payload;
      }
    }
  },
});

export const {
  setLoading,
  setError,
  setMobileNumber,
  setOtpSent,
  setOtpVerified,
  loginSuccess,
  logout,
  clearError,
  setGooglePrefillData,
  clearGooglePrefillData,
  setEmailVerified
} = authSlice.actions;

export default authSlice.reducer;